import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Grid, Headline, SelectablePanel, Type, Utility } from "mesh-component-library";
import _isEmpty from "lodash/isEmpty";
import { ContinueAsPpuModal } from "../continue-as-ppu-modal/ContinueAsPpuModal";
import { useLocale } from "../../contexts/LocaleContext";
import { useScreenBreakpoint } from "../../hooks/useScreenBreakpoint";
import { generatePath } from "../../utilities/RedirectUtils";
import { TRANSLATION_CONSTANTS } from "../../app-consts/translations";
import { Services } from "../../types/Service";
import { useDispatch } from "react-redux";
import { updateCurrentLocation, updateTowReason } from "../../action";

export type MechanicalIssueTriageProps = {
  clearCCardInfo: () => void;
  clearPreviousBenefits: () => void;
  commonBenefits: any;
  commonBenefitsSuccess: (data: any) => void;
  displayCode: string;
  noCommonBenefitsWithPrevious: (data: any) => void;
  serviceInfo: any;
  updateCCardInfo: (data: any) => void;
  updateLoggedInFlag: (loggedInFlag: string) => void;
  updatePpuFlag: (ppuFlag: string) => void;
  towCoverages: any;
  ppuFlag: string;
};

export const MechanicalIssueTriage = ({
  clearCCardInfo,
  clearPreviousBenefits,
  commonBenefits,
  commonBenefitsSuccess,
  displayCode = "",
  noCommonBenefitsWithPrevious,
  serviceInfo,
  updateCCardInfo,
  updatePpuFlag,
  updateLoggedInFlag,
  towCoverages,
  ppuFlag,
}: MechanicalIssueTriageProps) => {
  const getTranslatedText = useLocale();
  const history = useHistory();
  const screenBreakpoint = useScreenBreakpoint();
  const dispatch = useDispatch();

  const [selectedReason, setSelectedReason] = useState("");
  const [showContinueAsPpuModal, setShowContinueAsPpuModal] = useState(false);

  useEffect(() => {
    const previousBenefits = commonBenefits?.previousBenefits;
    if (!!previousBenefits && previousBenefits?.benefits) {
      updatePpuFlag("N");
      updateLoggedInFlag("Y");
      clearCCardInfo();
      commonBenefitsSuccess(previousBenefits);
      clearPreviousBenefits();
    }
  }, []);

  const routeToDisablementLocation = () => {
    dispatch(updateTowReason(selectedReason));
    dispatch(updateCurrentLocation({ setFromLocationServices: false }));
    history.push(generatePath("disablementlocation"));
  };

  const handleOnClickContinue = () => {             
    if (_isEmpty(towCoverages) || ppuFlag === "Y") {
      routeToDisablementLocation();
      return;
    }
    const hasCoveredListed = towCoverages.every(({ status }) => status === "covered");
    const hasNotCoveredListed = towCoverages.every(({ status }) => status === "notCovered");
    const coverageSelectedReason = towCoverages.find(({ reason }) => reason === selectedReason);

    if (hasCoveredListed) {
      if (!!coverageSelectedReason) {
        routeToDisablementLocation();
      } else {
        setShowContinueAsPpuModal(true);
      }
      return;
    }

    if (hasNotCoveredListed) {
      if (!!coverageSelectedReason) {
        setShowContinueAsPpuModal(true);
      } else {
        routeToDisablementLocation();
      }
    }
  };

  const handleOnClickContinueAsGuest = () => {
    updatePpuFlag("Y");
    updateLoggedInFlag("N");
    updateCCardInfo({ transactionAmount: serviceInfo.originalCost });
    noCommonBenefitsWithPrevious({
      message: "",
      response: commonBenefits,
    });
    setShowContinueAsPpuModal(false);
    routeToDisablementLocation();
  };

  const handleOnClickCancel = () => {
    setShowContinueAsPpuModal(false);
  };

  const MechanicalIssueTriageReasons = [
    {
      id: "brakes",
      reason: getTranslatedText(TRANSLATION_CONSTANTS.MECHANICAL_BRAKING_PROBLEM),
    },
    {
      id: "dash_warning_light_on_or_flashing",
      reason: getTranslatedText(TRANSLATION_CONSTANTS.MECHANICAL_DASH_WARNING_LIGHT_ON),
    },
    {
      id: "engine_performance",
      reason: getTranslatedText(TRANSLATION_CONSTANTS.MECHANICAL_ENGINE_PERFORMANCE),
    },
    {
      id: "gear_shift_stuck_broken",
      reason: getTranslatedText(TRANSLATION_CONSTANTS.MECHANICAL_GEAR_SHIFT_TRANSMISSION_PROBLEM),
    },
    {
      id: "headlights_inoperable",
      reason: getTranslatedText(TRANSLATION_CONSTANTS.MECHANICAL_HEADLIGHTS_PROBLEM),
    },
    {
      id: "leaking_fluid",
      reason: getTranslatedText(TRANSLATION_CONSTANTS.MECHANICAL_LEAKING_FLUID),
    },
    {
      id: "overheating",
      reason: getTranslatedText(TRANSLATION_CONSTANTS.MECHANICAL_OVERHEATING),
    },
    {
      id: "mechanical_steering_suspension",
      reason: getTranslatedText(TRANSLATION_CONSTANTS.MECHANICAL_STEERING_SUSPENSION_PROBLEM),
    },
    {
      id: "windshield_wipers_inoperable",
      reason: getTranslatedText(TRANSLATION_CONSTANTS.MECHANICAL_WINDSHIELD_WIPERS_PROBLEM),
    },
  ];

  return (
    <>
      <Grid verticalAlign="middle" id="mechanical-issue-triage-page">
        <Grid.Column col={{ sm: 12, md: 4, lg: 4, xl: 4 }} offset={{ md: 4, lg: 4, xl: 4 }}>
          <Utility.Div
            utils={{
              hrLeft: "md",
              hrRight: "md",
              vrTop: "md",
              vrBottom: "md",
            }}
          >
            <Headline
              title={
                <Type variant="darker" align="left" size={screenBreakpoint === "sm" ? "7xl" : "3xl"} weight="semibold">
                  {getTranslatedText(TRANSLATION_CONSTANTS.MECHANICAL_ISSUE_TRIAGE_TITLE)}
                </Type>
              }
            />
          </Utility.Div>
        </Grid.Column>
        <Grid.Column col={{ sm: 12, md: 4, lg: 4, xl: 4 }} offset={{ md: 4, lg: 4, xl: 4 }}>
          <Utility.Div
            utils={{
              hrLeft: "md",
              hrRight: "md",
            }}
          >
            {MechanicalIssueTriageReasons.map(({ id: reasonId, reason }) => (
              <SelectablePanel
                key={`mechanical-issue-triage-reason-${reasonId}`}
                id={reasonId}
                checked={selectedReason === reasonId}
                hasBorder
                showInput
                text={reason}
                type="radio"
                onChange={(event) => {
                  setSelectedReason(event.currentTarget.id);
                }}
              />
            ))}
          </Utility.Div>
        </Grid.Column>
        <Grid.Column
          col={{ sm: 12, md: 4, lg: 4, xl: 4 }}
          offset={{ md: 4, lg: 4, xl: 4 }}
          utils={{
            vrTop: "md",
          }}
        >
          <Utility.Div
            utils={{
              hrLeft: "md",
              hrRight: "md",
            }}
          >
            <Button
              hasUpgrade
              shape="pill"
              size="lg"
              onClick={handleOnClickContinue}
              isBlock
              disabled={_isEmpty(selectedReason)}
            >
              {getTranslatedText(TRANSLATION_CONSTANTS.CONTINUE, displayCode)}
            </Button>
          </Utility.Div>
        </Grid.Column>
      </Grid>
      <ContinueAsPpuModal
        active={showContinueAsPpuModal}
        content={getTranslatedText(TRANSLATION_CONSTANTS.TOW_REASON_NOT_COVERED_MSG)}
        primaryButton={{
          value: getTranslatedText(TRANSLATION_CONSTANTS.CONTINUE_AS_GUEST),
          onClick: handleOnClickContinueAsGuest,
          testId: "continue-as-guest-primary-button",
        }}
        closeButton={{
          onClick: handleOnClickCancel,
          testId: "continue-as-guest-close-button",
        }}
      />
    </>
  );
};
